import { useState } from 'react';
import { LaborCategory, LaborRate } from '../../types/pmTypes';
import { createLaborCategory, createLaborRate, updateLaborCategory } from '../../graphql/companyResourceQueries';
import { Btn, BtnIconOnly, IconClose, InputField } from '../ui';

interface LaborRateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => Promise<void>;
    editCategory: LaborCategory | null;
    currentRate: LaborRate | null;
    companyId: string;
}

export const LaborRateModal = ({ isOpen, onClose, onSave, editCategory, currentRate, companyId }: LaborRateModalProps) => {
    const [name, setName] = useState(editCategory?.name || '');
    const [standardRate, setStandardRate] = useState(currentRate?.standardRate?.toString() || '');
    const [overtimeRate, setOvertimeRate] = useState(currentRate?.overtimeRate?.toString() || '');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    if (!isOpen) return null;

    const getLocalTodayStr = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    };

    const validate = () => {
        const errs: Record<string, string> = {};
        if (!name.trim()) errs.name = 'Labor type name is required';
        if (!standardRate || isNaN(parseFloat(standardRate))) errs.standardRate = 'Valid ST rate is required';
        if (!overtimeRate || isNaN(parseFloat(overtimeRate))) errs.overtimeRate = 'Valid OT rate is required';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const stRate = parseFloat(standardRate);
            const otRate = parseFloat(overtimeRate);
            const today = getLocalTodayStr();

            if (editCategory) {
                // Update name if changed
                if (name.trim() !== editCategory.name) {
                    await updateLaborCategory({ id: editCategory.id, name: name.trim() });
                }
                // If rates changed, create new rate record (preserving history)
                const ratesChanged = !currentRate ||
                    stRate !== currentRate.standardRate ||
                    otRate !== currentRate.overtimeRate;
                if (ratesChanged) {
                    await createLaborRate({
                        categoryId: editCategory.id,
                        standardRate: stRate,
                        overtimeRate: otRate,
                        effectiveDate: new Date(today + 'T00:00:00').toISOString(),
                    });
                }
            } else {
                // Create new category + initial rate
                const newCat = await createLaborCategory({
                    companyId,
                    name: name.trim(),
                    isActive: true,
                });
                if (!newCat) throw new Error('Failed to create labor category.');
                await createLaborRate({
                    categoryId: newCat.id,
                    standardRate: stRate,
                    overtimeRate: otRate,
                    effectiveDate: new Date(today + 'T00:00:00').toISOString(),
                });
            }
            await onSave();
        } catch (err) {
            console.error('Error saving labor rate:', err);
            setErrors({ _form: 'Failed to save. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="cr-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="cr-modal" onClick={e => e.stopPropagation()}>
                <div className="cr-modal-header">
                    <h2 className="cr-modal-title">{editCategory ? 'Edit Labor Rate' : 'Add Labor Rate'}</h2>
                    <BtnIconOnly size="sm" icon={<IconClose />} ariaLabel="Close" onClick={onClose} />
                </div>
                <div className="cr-modal-body">
                    {errors._form && <div className="cr-error-banner">⚠️ {errors._form}</div>}

                    <div className="cr-field">
                        <InputField
                            label="Labor Type *"
                            inputId="labor-rate-name"
                            value={name}
                            onChange={setName}
                            placeholder="e.g., Arizona - Journeyman"
                            hasError={!!errors.name}
                            ariaDescribedBy={errors.name ? 'labor-rate-name-error' : undefined}
                        />
                        {errors.name && <div id="labor-rate-name-error" className="cr-field-error-text">{errors.name}</div>}
                    </div>

                    <div className="cr-field">
                        <InputField
                            label="ST Rate/HR ($) *"
                            inputId="labor-rate-st"
                            type="number"
                            step="0.01"
                            min={0}
                            value={standardRate}
                            onChange={setStandardRate}
                            hasError={!!errors.standardRate}
                            ariaDescribedBy={errors.standardRate ? 'labor-rate-st-error' : undefined}
                        />
                        {errors.standardRate && <div id="labor-rate-st-error" className="cr-field-error-text">{errors.standardRate}</div>}
                    </div>

                    <div className="cr-field">
                        <InputField
                            label="OT Rate/HR ($) *"
                            inputId="labor-rate-ot"
                            type="number"
                            step="0.01"
                            min={0}
                            value={overtimeRate}
                            onChange={setOvertimeRate}
                            hasError={!!errors.overtimeRate}
                            ariaDescribedBy={errors.overtimeRate ? 'labor-rate-ot-error' : undefined}
                        />
                        {errors.overtimeRate && <div id="labor-rate-ot-error" className="cr-field-error-text">{errors.overtimeRate}</div>}
                    </div>

                    {editCategory && currentRate && (
                        <div className="cr-info-banner" style={{ marginTop: 8 }}>
                            <span className="cr-info-banner-icon">ℹ️</span>
                            Changing rates creates a new rate record with today's date. Previous rates are preserved for historical accuracy.
                        </div>
                    )}
                </div>
                <div className="cr-modal-footer">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                    <Btn
                        variant="primary"
                        buttonText={saving ? 'Saving...' : (editCategory ? 'Update' : 'Create')}
                        buttonOnClick={handleSubmit}
                        buttonDisabled={saving}
                    />
                </div>
            </div>
        </div>
    );
};
